import { memo, useLayoutEffect, useRef } from 'react'
import { Search } from 'lucide-react'
import { useDisplayReports } from '../../../app/home/displayReport'
import { fmtTemp } from '../../../shared/units'
import { usePlaces, selectPlace } from '../../store'
import type { ZoneProps } from '../../data/types'
import { frameAtCursor, writeText } from '../../shell/bindings'
import { timeCursor } from '../../../app/time/cursor'
import { openPlaces } from '../../shell/actions'
import { WeatherGlyph } from '../../shell/WeatherGlyph'
import { WX_MARKUP } from '../../icons/catalog'
import { wxIconId } from '../../icons/codes'
import { usePhonePlaceSwipe } from './phoneSwipe'
import { locationDetail } from './placeInfo'
import './places.css'

export const Places = memo(function Places({ data }: ZoneProps) {
  const places = usePlaces()
  const reports = useDisplayReports(places)
  const ref = useRef<HTMLElement>(null)
  usePhonePlaceSwipe()
  useLayoutEffect(() => {
    const selected = ref.current?.querySelector('[aria-current="location"]')
    const temperature = selected?.querySelector('[data-place-temperature]'), description = selected?.querySelector('[data-place-description]')
    const glyph = selected?.querySelector<SVGElement>('[data-wx-icon]')
    const update = () => {
      const frame = frameAtCursor(data)
      if (!frame) return
      writeText(temperature ?? null, frame.temperature)
      writeText(description ?? null, `${timeCursor.isNow() ? 'Now' : frame.stamp} · ${frame.description}${data.mock ? ' · Demo' : ''}`)
      const key = wxIconId(frame.condition, frame.night).replace('wx-v5-', '') + '-compact'
      if (glyph && glyph.dataset.wxIcon !== key) { glyph.innerHTML = WX_MARKUP[key]; glyph.dataset.wxIcon = key }
    }
    update(); return timeCursor.subscribe(update)
  }, [data, places])
  return <nav className="v5-places" data-zone="places" aria-label="Saved places" ref={ref}><div className="v5-places-scroll">{places.map((place, i) => {
    const report = place.id === data.place.id ? data.report : reports[i]?.data
    return <button key={place.id} data-qa="place" aria-current={place.id === data.place.id ? 'location' : undefined} onClick={() => selectPlace(place.id)} title={`${place.label} · ${locationDetail(place, data.units)}${data.offline ? ' · saved forecast' : ''}`}>
      <span className="v5-strip-place-name">{place.isCurrent ? 'My Location' : place.name}</span>{report ? <WeatherGlyph condition={report.current.condition} night={!report.current.is_day} size={18} /> : <span className="v5-strip-icon-gap" />}<strong data-place-temperature>{place.id === data.place.id ? data.now?.temperature ?? '—' : fmtTemp(report?.current.temperature_c, data.units.temp)}</strong><span className="v5-chrome-sr" data-place-description>Now · {report?.current.description ?? (data.offline ? 'No saved weather' : 'Weather not loaded')}{data.mock ? ' · Demo' : ''}</span>
    </button>
  })}</div><button className="v5-place-search" onClick={openPlaces} aria-label="Find a place"><span className="v5-search-word">Find a place</span><Search size={18} aria-hidden="true" /></button></nav>
})
